import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { RetentionCohort, RetentionLatest } from '../../types/analytics';

interface RetentionTableProps {
  cohorts: RetentionCohort[];
  latest: RetentionLatest | null;
}

function formatRate(returned: number | null, cohortSize: number): string {
  if (returned === null) return '—';
  return `${((returned / cohortSize) * 100).toFixed(1)}%`;
}

export default function RetentionTable({ cohorts, latest }: RetentionTableProps) {
  return (
    <Card className="bg-admin-dark-800 border-admin-slate-300/10">
      <CardHeader>
        <CardTitle className="text-heading-5 text-admin-slate-400">Retention Cohorts</CardTitle>
        {latest && (
          <div className="flex gap-4 mt-2">
            <span className="text-xs text-admin-slate-500">
              D1: <span className="text-admin-accent-blue font-medium">{latest.d1Rate != null ? `${(latest.d1Rate * 100).toFixed(1)}%` : '—'}</span>
            </span>
            <span className="text-xs text-admin-slate-500">
              D7: <span className="text-admin-accent-purple font-medium">{latest.d7Rate != null ? `${(latest.d7Rate * 100).toFixed(1)}%` : '—'}</span>
            </span>
            <span className="text-xs text-admin-slate-500">
              D30: <span className="text-admin-accent-green font-medium">{latest.d30Rate != null ? `${(latest.d30Rate * 100).toFixed(1)}%` : '—'}</span>
            </span>
          </div>
        )}
      </CardHeader>
      <CardContent>
        {cohorts.length === 0 ? (
          <p className="text-admin-slate-500 text-sm">No retention data available.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-admin-slate-300/10">
                  <th className="text-left py-2 px-3 text-admin-slate-500 font-medium">Cohort</th>
                  <th className="text-right py-2 px-3 text-admin-slate-500 font-medium">Size</th>
                  <th className="text-right py-2 px-3 text-admin-slate-500 font-medium">D1</th>
                  <th className="text-right py-2 px-3 text-admin-slate-500 font-medium">D7</th>
                  <th className="text-right py-2 px-3 text-admin-slate-500 font-medium">D30</th>
                </tr>
              </thead>
              <tbody>
                {cohorts.map((cohort) => (
                  <tr key={cohort.id} className="border-b border-admin-slate-300/5 hover:bg-admin-dark-700/50">
                    <td className="py-2 px-3 text-admin-slate-400">{cohort.cohortDate.split('T')[0]}</td>
                    <td className="py-2 px-3 text-right text-admin-slate-400">{cohort.cohortSize}</td>
                    <td className="py-2 px-3 text-right text-admin-accent-blue">{formatRate(cohort.returnedD1, cohort.cohortSize)}</td>
                    <td className="py-2 px-3 text-right text-admin-accent-purple">{formatRate(cohort.returnedD7, cohort.cohortSize)}</td>
                    <td className="py-2 px-3 text-right text-admin-accent-green">{formatRate(cohort.returnedD30, cohort.cohortSize)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
